import React from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import "./MatchReasonsPopover.scss";

// undefined, not null, so OverlayTrigger falls back to its inline default.
const PORTAL_TARGET =
  typeof document !== "undefined" ? document.body : undefined;

const EDGE_PAD = 16;
const MIN_PANEL_H = 180;

// Cap the panel to the space on the chosen side. Without this, a tall panel on
// the first card cannot fit above and Popper drops it over the card it is
// explaining; shrinking lets "top" stay viable and the body scrolls instead.
const fitAvailableHeight = {
  name: "fitAvailableHeight",
  enabled: true,
  phase: "beforeWrite",
  requiresIfExists: ["preventOverflow", "flip"],
  fn({ state }) {
    const ref = state.elements.reference.getBoundingClientRect();
    const vh = window.innerHeight;
    let space;
    if (state.placement.startsWith("top")) space = ref.top - EDGE_PAD;
    else if (state.placement.startsWith("bottom"))
      space = vh - ref.bottom - EDGE_PAD;
    else space = vh - EDGE_PAD * 2;
    state.elements.popper.style.setProperty(
      "--mrp-max-h",
      `${Math.max(MIN_PANEL_H, Math.round(space))}px`,
    );
  },
};

const REASON_GROUPS = [
  "matched",
  "partial",
  "missing",
  "must_haves_matched",
  "must_haves_missing",
];

// Nice-to-have chips read from whichever side the viewer owns: on a
// Want-Listing's matches the viewer is the buyer's agent, on a listing's
// matches they hold the inventory.
const CHIP_LABELS = {
  buyer: { matched: "You want, this has:", missing: "You want, this lacks:" },
  seller: {
    matched: "They want, you have:",
    missing: "They want, you don't have:",
  },
};

const hasAnyReason = (reasons) =>
  !!reasons && REASON_GROUPS.some((group) => reasons[group]?.length > 0);

const ReasonList = ({ items, variant, icon }) => {
  if (!items?.length) return null;
  return (
    <ul
      className={`match-reasons-popover__list match-reasons-popover__list--${variant}`}
    >
      {items.map((reason, i) => (
        <li key={reason.key || `${variant}-${i}`}>
          <span className="match-reasons-popover__icon" aria-hidden="true">
            {icon}
          </span>
          <span>{reason.text}</span>
        </li>
      ))}
    </ul>
  );
};

const ChipSection = ({ label, items, variant }) => {
  if (!items?.length) return null;
  return (
    <div className="match-reasons-popover__chip-section">
      <p className="match-reasons-popover__chip-label">{label}</p>
      <div className="match-reasons-popover__chips">
        {items.map((text, i) => (
          <span
            key={`${variant}-${i}`}
            className={`match-reasons-popover__chip match-reasons-popover__chip--${variant}`}
          >
            {text}
          </span>
        ))}
      </div>
    </div>
  );
};

const MatchReasonsPopover = ({
  reasons,
  score,
  children,
  // The badge sits at the top of its card, so opening downward covers the very
  // card being explained. Open upward instead.
  placement = "top",
  perspective = "buyer",
  // { reason, aiScore, isWeak } from useAiRanking — absent renders today's UI.
  ai = null,
}) => {
  const aiReason = typeof ai?.reason === "string" ? ai.reason : null;
  if (!hasAnyReason(reasons) && !aiReason) return children ?? null;

  const numericScore = Math.round(parseFloat(score));
  const showScore = Number.isFinite(numericScore);
  const chipLabels = CHIP_LABELS[perspective] || CHIP_LABELS.buyer;

  // Three-state nice-to-haves: the feed's structured tags say "lacks", but the
  // AI may have found the feature in the listing's own description. Those move
  // out of the strikethrough group into their own "mentioned" section.
  const aiFound = new Set(
    (Array.isArray(ai?.n2hFound) ? ai.n2hFound : []).map((l) =>
      String(l).toLowerCase(),
    ),
  );
  const rawMissing = reasons?.must_haves_missing || [];
  const mentioned = rawMissing.filter((l) =>
    aiFound.has(String(l).toLowerCase()),
  );
  const trulyMissing = rawMissing.filter(
    (l) => !aiFound.has(String(l).toLowerCase()),
  );

  // With an AI verdict the header speaks in plain words; the exact
  // "N of 6 criteria" count lives on in the breakdown toggle below.
  const aiScoreNum = Number(ai?.aiScore);
  const fitLabel = aiReason
    ? ai?.isWeak
      ? "A stretch for this buyer"
      : aiScoreNum >= 90
        ? "Exceptional fit"
        : aiScoreNum >= 78
          ? "Strong fit"
          : aiScoreNum >= 60
            ? "Good fit"
            : "Fair fit"
    : null;

  const bullets = (
    <>
      <ReasonList items={reasons?.matched} variant="matched" icon="✓" />
      <ReasonList items={reasons?.partial} variant="partial" icon="~" />
      <ReasonList items={reasons?.missing} variant="missing" icon="✗" />
    </>
  );

  const popover = (
    <Popover className="match-reasons-popover">
      <Popover.Header className="match-reasons-popover__header">
        {showScore && (
          <span className="match-reasons-popover__score">{numericScore}%</span>
        )}
        {fitLabel ? (
          <span
            className={`match-reasons-popover__fit${ai?.isWeak ? " match-reasons-popover__fit--weak" : ""}`}
          >
            {fitLabel}
          </span>
        ) : (
          reasons?.summary && (
            <span className="match-reasons-popover__summary">
              {reasons.summary}
            </span>
          )
        )}
      </Popover.Header>
      <Popover.Body className="match-reasons-popover__body">
        {aiReason && (
          <div
            className={`match-reasons-popover__ai${ai?.isWeak ? " match-reasons-popover__ai--weak" : ""}`}
          >
            <span className="match-reasons-popover__ai-head">
              <span
                className="match-reasons-popover__ai-spark"
                aria-hidden="true"
              >
                ✦
              </span>
              Why this home
              <span className="match-reasons-popover__ai-badge">AI</span>
            </span>
            <span className="match-reasons-popover__ai-text">{aiReason}</span>
          </div>
        )}
        <ChipSection
          label={chipLabels.matched}
          items={reasons?.must_haves_matched}
          variant="yes"
        />
        <ChipSection
          label="Not tagged, but mentioned in the description:"
          items={mentioned}
          variant="mentioned"
        />
        <ChipSection
          label={chipLabels.missing}
          items={trulyMissing}
          variant="no"
        />
        {aiReason ? (
          // The numbers still exist for whoever wants them — one tap away
          // instead of front and center.
          <details className="match-reasons-popover__breakdown">
            <summary>
              {reasons?.summary
                ? `Criteria breakdown — ${String(reasons.summary).toLowerCase()}`
                : "Criteria breakdown"}
            </summary>
            {bullets}
          </details>
        ) : (
          bullets
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      placement={placement}
      trigger={["hover", "focus", "click"]}
      rootClose
      delay={{ show: 150, hide: 200 }}
      flip
      // Out of the feed's scroll container it cannot stretch anyone's scroll
      // extents, so the scrollbars stop blinking on the frame before Popper
      // positions it.
      container={PORTAL_TARGET}
      popperConfig={{
        strategy: "fixed",
        modifiers: [
          {
            name: "flip",
            // Sides before "bottom": on the first card there is no room above,
            // and dropping down would hide the card the panel is describing.
            options: {
              fallbackPlacements: ["top", "right", "left", "bottom"],
              padding: 8,
            },
          },
          // altAxis lets it slide along the edge to stay on screen instead of
          // giving up and flipping into the card.
          {
            name: "preventOverflow",
            options: { padding: 8, altAxis: true },
          },
          fitAvailableHeight,
        ],
      }}
      overlay={popover}
    >
      <span
        className="match-reasons-popover__trigger"
        role="button"
        tabIndex={0}
        aria-label="Why this matched"
        // Match cards are clickable; stop the badge from navigating on tap.
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default MatchReasonsPopover;
